import {
  createContext,
  FC,
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useCallback,
  useState,
} from "react";
import { SplashScreen } from "src/components/splash-screen";
import { Household, UserDataObj } from "src/types/snugtotal";
import {
  GenericPath,
  useV3GenericGetData,
  useV3Update,
  useV3Create,
} from "src/api/snugtotal/estate_app";
import { useRouter } from "src/hooks/use-router";
import { usePathname } from "src/hooks/use-pathname";
import {
  PaymentRequiredData,
  useStripeStore,
} from "src/hooks/snug/use-payments";
import { useTermsAcceptance } from "src/hooks/snug/use-terms-acceptance";
import { TermsAcceptanceDialog } from "src/components/terms-acceptance-dialog";
// import path from "path";

interface HouseholdProviderProps {
  children: ReactNode;
  userData: UserDataObj | null;
  ownerUserData: UserDataObj | null;
  updateProHouseholdContext: (household: Household) => void;
}

// create experience flag enum
export enum ExperienceFlag {
  HEAD_OF_HOUSEHOLD = "HEAD_OF_HOUSEHOLD",
  HOUSEHOLD_MEMBER = "HOUSEHOLD_MEMBER",
  READONLY = "READONLY",
  PROFESSIONAL = "PROFESSIONAL",
}

type HouseholdStoreAction =
  | { type: "SET_HOUSEHOLDS"; payload: Household[] }
  | { type: "SET_SELECTED_HOUSEHOLD_ID"; payload: string | null }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_PAYMENT_REQUIRED_SUCCESS"; payload: boolean }
  | { type: "SET_CHECKED"; payload: boolean };

interface HouseholdContextState {
  households: Household[];
  selectedHousehold: Household | null;
  selectedHouseholdId: string | null;
  loading: boolean;
  selectedUdId: string | null;
  headOfHouseholDataSaving: boolean;
  checked: boolean;
}

interface HouseholdContextValue extends HouseholdContextState {
  experienceFlag: ExperienceFlag;
  selectHousehold: (householdId: string) => void;
  setSelectedUdId: (udid: string) => void;
  refreshHouseholds: () => Promise<void>;
  onboardingHandleEditHoHUserData: (
    hoh_ud_id: string,
    userData: UserDataObj
  ) => Promise<void>;
  // Stripe store properties (following userData-context pattern)
  paymentRequiredData: any;
  paymentRequiredLoading: boolean;
  paymentsSaving: boolean;
  currentPaymentIntent: any;
  // paymentMethods: PaymentMethod[];
  // paymentMethodsLoading: boolean;
  handleCreateOrUpdatePaymentIntent: (...args: any[]) => Promise<any>;
  handleClearPaymentIntent: () => void;
  handleGetPaymentRequired: (
    ud_id: string | null,
    product: string
  ) => Promise<any>;
  updatePaymentRequiredForBuilder: (
    ud_id: string | null,
    product: string
  ) => Promise<void>;
  // handleGetPaymentMethods: (ud_id: string | null) => Promise<any>;
  refreshPaymentRequired: () => Promise<void>;
  createSubscription: (...args: any[]) => Promise<any>;
  createSetupIntent: (ud_id: string) => Promise<any>;
  createTrialSubscription: (...args: any[]) => Promise<any>;
  cancelSubscription: (...args: any[]) => Promise<any>;
  retrieveSubscription: (ud_id: string) => Promise<any>;
}

const householdReducer = (
  state: HouseholdContextState,
  action: HouseholdStoreAction
): HouseholdContextState => {
  switch (action.type) {
    case "SET_HOUSEHOLDS": {
      const households = action.payload;
      const selectedHousehold =
        households?.find(
          (h) => h.household_role?.id === state.selectedHouseholdId
        ) ||
        households[0] ||
        null;
      return {
        ...state,
        households,
        selectedHousehold,
        selectedHouseholdId: selectedHousehold?.household_role?.id || null,
        selectedUdId: selectedHousehold?.head_of_household?.ud_id || null,
      };
    }
    case "SET_SELECTED_HOUSEHOLD_ID": {
      const selectedHousehold =
        state.households?.find(
          (h) => h.household_role?.id === action.payload
        ) ||
        state.households[0] ||
        null;
      return {
        ...state,
        selectedHouseholdId: action.payload,
        selectedHousehold,
        selectedUdId: selectedHousehold?.head_of_household?.ud_id || null,
      };
    }
    case "SET_LOADING":
      return { ...state, loading: action.payload };
    case "SET_CHECKED":
      return { ...state, checked: action.payload };
    default:
      return state;
  }
};

const HouseholdContext = createContext<HouseholdContextValue>({
  households: [],
  selectedHousehold: null,
  selectedHouseholdId: null,
  loading: true,
  selectedUdId: null,
  experienceFlag: ExperienceFlag.HEAD_OF_HOUSEHOLD,
  headOfHouseholDataSaving: false,
  paymentRequiredData: null,
  paymentRequiredLoading: false,
  paymentsSaving: false,
  currentPaymentIntent: null,
  selectHousehold: () => {},
  setSelectedUdId: () => {},
  refreshHouseholds: async () => {},
  onboardingHandleEditHoHUserData: async () => {},

  handleCreateOrUpdatePaymentIntent: async () => null,
  handleClearPaymentIntent: () => {},
  handleGetPaymentRequired: async (_ud_id: string | null, _product: string) =>
    null,
  updatePaymentRequiredForBuilder: async (_ud_id: string | null, _product: string) => {},
  // handleGetPaymentMethods: async (_ud_id: string | null) => null,
  refreshPaymentRequired: async () => {},
  createSubscription: async () => null,
  createSetupIntent: async (_ud_id: string) => null,
  createTrialSubscription: async () => null,
  cancelSubscription: async () => null,
  retrieveSubscription: async (_ud_id: string) => null,
  checked: false,
});

export const HouseholdProvider: FC<HouseholdProviderProps> = (props) => {
  const { children, userData, ownerUserData, updateProHouseholdContext } =
    props;
  const router = useRouter();
  const pathname = usePathname();
  const [state, dispatch] = useReducer(householdReducer, {
    households: [],
    selectedHousehold: null,
    selectedHouseholdId: null,
    loading: true,
    selectedUdId: null,
    headOfHouseholDataSaving: false,
    checked: false,
  });

  const [headOfHouseholDataSaving, setHeadOfHouseholDataSaving] =
    useState(false);
  const [experienceFlag, setExperienceFlag] = useState<ExperienceFlag>(
    ExperienceFlag.HEAD_OF_HOUSEHOLD
  );
  const [selectedUdId, setSelectedUdId] = useState<string | null>(null);

  const getHouseholds = useV3GenericGetData<Household[]>(
    GenericPath.GENERIC_ENTITY_LIST_CREATE_V3
  );

  const getHousehold = useV3GenericGetData<Household>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  const updateUserData = useV3Update<UserDataObj>(
    GenericPath.GENERIC_ENTITY_RETRIEVE_UPDATE_DELETE_V3
  );

  const createTermsAcceptance = useV3Create(
    GenericPath.TERMS_ACCEPTANCE_V3
  );

  const {
    paymentRequiredData,
    paymentRequiredLoading,
    paymentsSaving,
    currentPaymentIntent,
    handleCreateOrUpdatePaymentIntent,
    handleClearPaymentIntent,
    handleGetPaymentRequired,
    refreshPaymentRequired,
    createSubscription,
    createSetupIntent,
    createTrialSubscription,
    cancelSubscription,
    retrieveSubscription,
  } = useStripeStore({
    ud_id: (state?.selectedHousehold?.household_role?.paywall_option === 'signup' ? state.selectedHousehold?.head_of_household?.ud_id : userData?.ud_id) || null,
    household: state.selectedHousehold,
  });

  // Terms acceptance - use ownerUserData.ud_id for household members, userData.ud_id otherwise
  const termsUdId = experienceFlag === ExperienceFlag.HOUSEHOLD_MEMBER
    ? ownerUserData?.ud_id
    : userData?.ud_id;

  const [bypassDialog, setBypassDialog] = useState(false);
  const [pendingProcessed, setPendingProcessed] = useState(false);

  const { hasAcceptedTerms, acceptTerms, loading: termsLoading } = useTermsAcceptance(termsUdId);

  // Process pending terms acceptance from verify-post-email
  useEffect(() => {
    const processPendingAcceptance = async () => {
      const pending = localStorage.getItem("terms_acceptance_pending");
      // Use ownerUserData.ud_id for pending acceptance (the actual logged-in user)
      const pendingUdId = ownerUserData?.ud_id;

      if (pending === "true" && pendingUdId && !pendingProcessed) {
        setPendingProcessed(true);

        try {
          // Call accept terms API now that UserData exists using proper API hook
          await createTermsAcceptance(
            {
              ud_id: pendingUdId,
              entity: "terms-acceptance",
            },
            {}
          );

          console.log("Processed pending terms acceptance for ud_id:", pendingUdId);

          // Set bypass flag so dialog doesn't show
          setBypassDialog(true);

          // Clear pending flag
          localStorage.removeItem("terms_acceptance_pending");
        } catch (error) {
          console.error("Error processing pending terms acceptance:", error);
          // If it fails, don't set bypass - let dialog show
          localStorage.removeItem("terms_acceptance_pending");
        }
      }
    };

    processPendingAcceptance();
  }, [ownerUserData?.ud_id, pendingProcessed, createTermsAcceptance]);

  const refreshHouseholds = useCallback(async () => {
    try {
      const response = await getHouseholds(
        {
          ud_id: userData?.ud_id || "",
          entity: "households",
        },
        {
          expand:
            "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,household_member_roles.role_target_user_data.estate_documents.documents,head_of_household.estate_documents,head_of_household.estate_documents.documents,head_of_household.household,head_of_household.total_payment_tier,household_role.pricing",
        }
      );
      dispatch({ type: "SET_HOUSEHOLDS", payload: response });
    } catch (error) {
      console.error(error);
    } finally {
    }
  }, [userData?.ud_id, getHouseholds]);

  const onboardingHandleEditHoHUserData = useCallback(
    async (hoh_ud_id: string, userData: UserDataObj): Promise<void> => {
      if (!hoh_ud_id) {
        console.error("No user data id provided");
        return Promise.reject("No user data id provided");
      }

      try {
        setHeadOfHouseholDataSaving(true);
        const params = { ud_id: hoh_ud_id, entity: "role-target-user-data" };
        const expandQuery = { expand: "professional_group_role,household" };
        await updateUserData(
          { ...params, entity_id: hoh_ud_id },
          userData,
          true,
          expandQuery
        );

        await refreshHouseholds();
      } catch (err) {
        console.error(err);
        throw err;
      } finally {
        setHeadOfHouseholDataSaving(false);
      }
    },
    [updateUserData, refreshHouseholds]
  );

  useEffect(() => {
    let isMounted = true;

    const fetchSingleHousehold = async (
      household_id: string
    ): Promise<Household | null> => {
      try {
        if (!household_id) {
          return null;
        }
        const response = await getHousehold(
          {
            ud_id: userData?.ud_id || "",
            entity: "household",
            entity_id: household_id,
          },
          {
            expand:
              "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,household_member_roles.role_target_user_data.estate_documents.documents,head_of_household.estate_documents,head_of_household.estate_documents.documents,head_of_household.household,head_of_household.total_payment_tier,household_role.pricing",
          }
        );
        if (sessionStorage.getItem("h_cc")) {
          updateProHouseholdContext(response);
        }
        if (isMounted) {
          await dispatch({ type: "SET_HOUSEHOLDS", payload: [response] });
          return response;
        }
        return null;
      } catch (error) {
        return null;
      } finally {
        if (isMounted) {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    };

    const fetchHouseholds = async () => {
      dispatch({ type: "SET_LOADING", payload: true });
      let isMounted = true;

      try {
        const response = await getHouseholds(
          {
            ud_id: userData?.ud_id || "",
            entity: "households",
          },
          {
            expand:
              "household_member_roles.role_target_user_data,household_member_roles.role_target_user_data.estate_documents,household_member_roles.role_target_user_data.estate_documents.documents,head_of_household.estate_documents,head_of_household.estate_documents.documents,head_of_household.household,head_of_household.total_payment_tier,household_role.pricing",
          }
        );
        if (isMounted) {
          dispatch({ type: "SET_HOUSEHOLDS", payload: response });
          //   set selected household to the first household
        }
      } catch (error) {
        console.error(error);
      } finally {
        if (isMounted) {
          dispatch({ type: "SET_LOADING", payload: false });
        }
      }
    };
    const proclient_ud_id = sessionStorage.getItem("h_cc");
    if (userData?.ud_id && !proclient_ud_id) {
      fetchHouseholds();
    } else if (userData?.ud_id && proclient_ud_id) {
      fetchSingleHousehold(proclient_ud_id);
    }
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.ud_id]);

  useEffect(() => {
    if (state.households && state.households.length > 0) {
      dispatch({
        type: "SET_SELECTED_HOUSEHOLD_ID",
        payload: state.households[0].household_role?.id || null,
      });
    }
  }, [state.households]);

  const selectHousehold = (householdId: string) => {
    dispatch({ type: "SET_SELECTED_HOUSEHOLD_ID", payload: householdId });
  };

  const check = useCallback(
    (houseHold: Household) => {
      
      const paymentCheck = (
        paymentProduct: string,
        paymentRequiredData: PaymentRequiredData | null
      ) => {
        // Use paymentRequiredData from state instead of async call
        const unpaid =
          ((paymentRequiredData?.trust_price || 0) > 0 &&
            paymentProduct === "TRUST") ||
          ((paymentRequiredData?.will_price || 0) > 0 &&
            paymentProduct === "WILL");

        const isAuthPath = pathname.startsWith("/auth");
        const isOnboardingPath = pathname.startsWith("/onboarding");

        if (!paymentRequiredData) {
          // Do not set CHECKED as we need to check again when payment data is available
          return;
        }
        if (unpaid && !isOnboardingPath && !isAuthPath) {
          router.replace("/onboarding?step=personal_plan");
        }
        dispatch({ type: "SET_CHECKED", payload: true });
        return true;
      };

      // Check for onboarding requirement
      if (!houseHold?.head_of_household?.estate_plan_foundation) {
        if (pathname !== "/onboarding") {
          router.replace("/onboarding");
          dispatch({ type: "SET_CHECKED", payload: true });
          return;
        }
      }

      if (houseHold?.household_role?.professional_pricing_option === "ON_PRO_TAB" && houseHold?.head_of_household?.household_state_code && houseHold?.head_of_household?.estate_plan_foundation) {
        // bypass onboarding
        dispatch({ type: "SET_CHECKED", payload: true });
        return;
      } else if (houseHold?.household_role?.professional_pricing_option === "ON_PRO_TAB" && !houseHold?.head_of_household?.household_state_code && houseHold?.head_of_household?.estate_plan_foundation) {
        // route to /onboarding
        if (pathname !== "/onboarding") {
          router.replace("/onboarding");
        }
        dispatch({ type: "SET_CHECKED", payload: true });
        return;
      }

      if (houseHold.household_role?.paywall_option === "signup") {
        // Determine if signup paywall should be active and what product to fetch
        const estatePlanFoundation =
          houseHold?.head_of_household?.estate_plan_foundation;
        const paymentProduct = estatePlanFoundation
            ? (estatePlanFoundation === "single_will" ||
              estatePlanFoundation === "two_wills")
            ? "WILL"
            : (estatePlanFoundation === "single_trust" ||
              estatePlanFoundation === "joint_trust" || 
              estatePlanFoundation === "two_trusts")
            ? "TRUST"
            : "WILL"
          : "";
        // Use Stripe hook for payment data management (only fetches when product and ud_id are set)
        return paymentCheck(paymentProduct, paymentRequiredData);
      }

      // View routing based on user type (legacy vs new)
      // Check if we should route to express vs dashboard based on sessionStorage or user state
      if (pathname === "/" || pathname === "/express") {
        const totalViewPreference = sessionStorage.getItem(
          "totalViewPreference"
        );

        if (totalViewPreference) {
          // Respect existing preference - map preference to actual path
          const preferredPath =
            totalViewPreference === "dashboard" ? "/" : "/express";

          if (pathname !== preferredPath) {
            router.replace(preferredPath);
            dispatch({ type: "SET_CHECKED", payload: true });
            return;
          }
          // Already on correct path for preference
        } else if (
          houseHold?.head_of_household?.total_payment_tier
            ?.subscription_status === "legacy" &&
          pathname === "/express"
        ) {
          // Legacy subscription - route to dashboard

          router.replace("/");
          dispatch({ type: "SET_CHECKED", payload: true });
          return;
        } else {
          dispatch({ type: "SET_CHECKED", payload: true });
          return;
        }
      }

      // set checked to true if no other checks needed
      dispatch({ type: "SET_CHECKED", payload: true });
      return;
    },
    [router, pathname, paymentRequiredData]
  );

  useEffect(() => {
    if (userData && state.selectedHousehold && !state.checked) {
      check(state.selectedHousehold);
    }
  }, [
    userData,
    check,
    state.checked,
    state.selectedHousehold,
    paymentRequiredData,
  ]);

  // Sync reducer state to local state
  useEffect(() => {
    if (state.selectedUdId !== selectedUdId) {
      setSelectedUdId(state.selectedUdId);
    }
  }, [state.selectedUdId, selectedUdId]);

  useEffect(() => {
    if (
      ownerUserData?.ud_id &&
      ownerUserData.ud_id === state.selectedHousehold?.head_of_household?.ud_id
    ) {
      setExperienceFlag(ExperienceFlag.HEAD_OF_HOUSEHOLD);
    } else if (
      ownerUserData?.ud_id &&
      ownerUserData.ud_id ===
        state.selectedHousehold?.professional_advisor?.ud_id &&
      ownerUserData.ud_id !== state.selectedHousehold?.head_of_household?.ud_id
    ) {
      setExperienceFlag(ExperienceFlag.PROFESSIONAL);
    } else if (
      ownerUserData?.ud_id &&
      state.selectedHousehold?.household_member_roles.find(
        (member) =>
          member.role_target_user_data_ud_id === ownerUserData.ud_id &&
          member.is_household_member
      )
    ) {
      setExperienceFlag(ExperienceFlag.HOUSEHOLD_MEMBER);
    } else if (
      ownerUserData?.ud_id &&
      state.selectedHousehold?.household_member_roles.find(
        (member) =>
          member.role_target_user_data_ud_id === ownerUserData.ud_id &&
          member.is_read_only
      )
    ) {
      setExperienceFlag(ExperienceFlag.READONLY);
    } else {
      setExperienceFlag(ExperienceFlag.HEAD_OF_HOUSEHOLD);
    }
  }, [ownerUserData, state.selectedHousehold]);

  // Function for builder component to update payment required when paywall is document_submission
  const updatePaymentRequiredForBuilder = useCallback(
    async (ud_id: string | null, product: string) => {
      const paywallOption = state.selectedHousehold?.household_role?.paywall_option;

      // Only update payment required if paywall_option is 'document_submission'
      if (paywallOption === 'document_submission' && ud_id && product) {
        await handleGetPaymentRequired(ud_id, product);
      }
    },
    [state.selectedHousehold, handleGetPaymentRequired]
  );

  if (state.loading || !state.checked) {
    return <SplashScreen />;
  }

  return (
    <HouseholdContext.Provider
      value={{
        ...state,
        experienceFlag,
        selectedUdId,
        headOfHouseholDataSaving,
        selectHousehold,
        setSelectedUdId,
        refreshHouseholds,
        onboardingHandleEditHoHUserData,
        // Stripe store properties passed directly (no dispatch needed)
        paymentRequiredData,
        paymentRequiredLoading,
        paymentsSaving,
        currentPaymentIntent,
        // paymentMethods,
        // paymentMethodsLoading,
        handleCreateOrUpdatePaymentIntent,
        handleClearPaymentIntent,
        handleGetPaymentRequired,
        updatePaymentRequiredForBuilder,
        // handleGetPaymentMethods,
        refreshPaymentRequired,
        createSubscription,
        createSetupIntent,
        createTrialSubscription,
        cancelSubscription,
        retrieveSubscription,
      }}
    >
      <TermsAcceptanceDialog
        open={hasAcceptedTerms === false && !termsLoading && !bypassDialog}
        onAccept={acceptTerms}
      />
      {children}
    </HouseholdContext.Provider>
  );
};

export default function useHousehold() {
  return useContext(HouseholdContext);
}

export const HouseholdConsumer = HouseholdContext.Consumer;
